/**
 * Split: move a misthreaded conversation off a ticket into a new one.
 *
 * ``useSplitSuggestion`` asks the server which comments belong to the
 * email thread that starts at a given customer email (its reply chain,
 * never internal notes). ``useSplitTicket`` runs the split.
 *
 * On success the whole ["agent", "tickets"] prefix is invalidated: a
 * split rewrites lists, counts, comments, events, watchers and
 * attachments on both tickets at once (same reasoning as the merge).
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import api from "@/lib/api";
import type {
  TicketMoveResponse,
  TicketSplitResponse,
  TicketSplitSuggestion,
} from "@/types/api";

export function useSplitSuggestion(
  ticketNumber: number,
  fromCommentId: string | null,
) {
  return useQuery({
    queryKey: ["agent", "tickets", ticketNumber, "split-suggestion", fromCommentId],
    queryFn: async (): Promise<TicketSplitSuggestion> => {
      const response = await api.get<TicketSplitSuggestion>(
        `/agent/tickets/${ticketNumber}/split-suggestion`,
        { params: { from_comment_id: fromCommentId } },
      );
      return response.data;
    },
    enabled: fromCommentId !== null,
    staleTime: 0,
    retry: false,
  });
}

export function useSplitTicket() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (args: {
      ticketNumber: number;
      commentIds: string[];
      subject: string;
    }): Promise<TicketSplitResponse> => {
      const response = await api.post<TicketSplitResponse>(
        `/agent/tickets/${args.ticketNumber}/split`,
        { comment_ids: args.commentIds, subject: args.subject || null },
      );
      return response.data;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["agent", "tickets"] });
    },
  });
}

/** Move messages to an existing ticket (the one-misfiled-reply case). */
export function useMoveComments() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (args: {
      ticketNumber: number;
      commentIds: string[];
      targetTicketNumber: number;
    }): Promise<TicketMoveResponse> => {
      const response = await api.post<TicketMoveResponse>(
        `/agent/tickets/${args.ticketNumber}/move-comments`,
        {
          comment_ids: args.commentIds,
          target_ticket_number: args.targetTicketNumber,
        },
      );
      return response.data;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["agent", "tickets"] });
    },
  });
}
